import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';

import {
    FormSelectChoice,
    VerificationService,
    FieldValidationErrors,
    MilitaryStatusResponse,
    Organization,
    InactiveMilitaryPersonalInfoResponse,
    ExtendedFieldValidationErrors,
    Metadata,
    logger,
    FormFooterComponent as FormFooter,
    VerificationStepsEnum,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    hasFailedInstantMatch,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    handleEmailOnKeyDown,
    submitForm,
    InactiveMilitaryPersonalInfoViewModel,
    BranchOfServiceComponent as BranchOfService,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    BirthDateComponent as BirthDate,
    EmailComponent as Email,
    MilitaryStatusDefaultMessagesEnum,
    resetRefs,
    setRef,
    DischargeDateComponent as DischargeDate,
    MarketConsentWrapper,
    FieldId,
    FieldIdEnum,
    FormValidationOptions,
    getCustomValidator,
    getCustomValidatorFields,
    getSafe,
    validateFieldById,
    ViewModel,
} from '@sheerid/jslib';

import { LogoHeaderComponent } from '../../LogoHeaderComponent';
import { PostalCodeComponent } from '../FormFields/PostalCode/PostalCodeComponent';
import { MilitaryStatusComponent as MilitaryStatus } from '../FormFields/MilitaryStatus/MilitaryStatusComponent';


interface StepInactiveMilitaryPersonalInfoComponentProps {
    intl: any;
    verificationService: VerificationService;
}

export const StepInactiveMilitaryPersonalInfo = ({ intl, verificationService }: StepInactiveMilitaryPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as InactiveMilitaryPersonalInfoViewModel;
    const previousViewModel = verificationService.previousViewModel as InactiveMilitaryPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as InactiveMilitaryPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

    const updateInactiveMilitaryViewModel = (key: keyof InactiveMilitaryPersonalInfoViewModel, value: string | Metadata) => {
        const nextState: InactiveMilitaryPersonalInfoViewModel = produce(viewModel, (draft: InactiveMilitaryPersonalInfoViewModel) => {
            // @ts-ignore
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const submitStep = () => {
        const nextState = produce(viewModel, () => {
        });
        logger.info('StepMilitaryStatusComponent submitting form');
        verificationService.submitStep(
            VerificationStepsEnum.collectMilitaryStatus,
            nextState,
            verificationService.previousVerificationResponse || verificationService.verificationResponse,
        );
    };

    const getAvailableMilitaryStatuses = ():FormSelectChoice[] => {
        const availableStatusesResponse =
            (verificationService.verificationResponse as MilitaryStatusResponse).availableStatuses ||
            (verificationService.previousVerificationResponse as MilitaryStatusResponse).availableStatuses;
        const availableStatuses: FormSelectChoice[] = [];

        for (const status of availableStatusesResponse) {
            availableStatuses.push({
                value: status,
                label: intl.formatMessage({ id: status, defaultMessage: MilitaryStatusDefaultMessagesEnum[status] }),
            });
        }
        return availableStatuses;
    };

    if ((viewModel.status && previousViewModel && previousViewModel.status !== viewModel.status) ||
        (viewModel.status && verificationService.verificationResponse.currentStep === VerificationStepsEnum.collectMilitaryStatus)) {
        submitStep();
    }

    const isFormFilled = (
        viewModel: ViewModel,
        formValidationOptions?: FormValidationOptions,
      ): boolean => {
        const customValidatorFields = getCustomValidatorFields();

        // TODO fix next line
        // eslint-disable-next-line
        for (const fieldId in viewModel) {
          const fieldValue = viewModel[fieldId] ? viewModel[fieldId] : "";
          const isFieldSkipped =
            viewModel.fieldsToSkipValidation &&
            viewModel.fieldsToSkipValidation.includes(fieldId as FieldId);

          if (
            FieldIdEnum[fieldId] &&
            typeof validateFieldById(fieldId, fieldValue, formValidationOptions) === "string" &&
            !isFieldSkipped
          ) {
            return false;
          }
        }

        const metadata = getSafe(() => (viewModel as any).metadata, null);
        if (metadata !== null) {
          // TODO fix next line
          // eslint-disable-next-line
          for (const fieldId in metadata) {
            const fieldValue = metadata[fieldId] !== undefined ? metadata[fieldId] : "";

            if (
              customValidatorFields.includes(fieldId) &&
              typeof getCustomValidator(fieldId)(fieldValue, formValidationOptions) === "string"
            ) {
              return false;
            }
          }
        }

        return true;
    };

    // Refs should be reset to ensure the field order isn't affected by the previous step
    resetRefs();

    return (
        <div id="sid-step-inactive-military-personal-info" className="sid-l-container">
            <LogoHeaderComponent/>
            { failedInstantMatch ?
                <div className="sid-header">

                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.title"
                            defaultMessage="Check Your Name &amp; Birthdate" />
                    </div>

                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Enter your first and last name on the form as it appears on your military document(s). Keep in mind that may be your given name." />
                    </div>
                </div>
            :
                <div className="sid-header">

                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.title"
                            defaultMessage="Unlock this Military-Only Offer" />
                    </div>

                    <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a member of the armed forces." />
                        &nbsp;
                        <HowDoesVerifyingWork verificationService={verificationService} />
                    </div>
                </div>
            }

            <div className="sid-header__title sid-l-space-top-lg">
                <h1>
                    <FormattedHTMLMessage
                        id="step.personalInfo.verifyYourDetails"
                        defaultMessage="Verify your details" />
                </h1>
            </div>

            <div className="sid-step-personalInfo-req-fields">
                <span>
                    <FormattedHTMLMessage
                        id="step.personalInfo.reqFields"
                        defaultMessage="All fields are required."/>
                </span>
            </div>

            <MilitaryStatus
                value={{
                    value: (verificationService.viewModel as InactiveMilitaryPersonalInfoViewModel).status,
                    label: MilitaryStatusDefaultMessagesEnum[(verificationService.viewModel as InactiveMilitaryPersonalInfoViewModel).status],
                }}
                isErrored={!!fieldValidationErrors.status}
                options={ getAvailableMilitaryStatuses() }
                placeholder={
                    intl.formatMessage({
                        id: 'militaryStatus',
                        defaultMessage: 'Select Status',
                    })
                }
                onChange={
                    (status: FormSelectChoice) => {
                        updateInactiveMilitaryViewModel('status', status ? (status.value as string) : '');
                        updateFieldValidationErrorsByFieldId('status', status ? (status.value as string) : '', verificationService);
                    }
                }
            />

            <BranchOfService
                autoFocus={viewModel.status && viewModel.organization.id === 0 ? true : false}
                isErrored={!!fieldValidationErrors.organization}
                organizations={ verificationService.orgList }
                placeholder={
                    intl.formatMessage({
                        id: "branchOfService",
                        defaultMessage: "Branch of Service",
                    })}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
                value={(verificationService.viewModel as InactiveMilitaryPersonalInfoViewModel).organization}
                verificationService={verificationService}
            />

            <div className="sid-names">
                <FirstName
                    placeholder={
                        intl.formatMessage({
                            id: "firstNamePlaceholder",
                            defaultMessage: "First Name",
                        })}
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={
                        (newValue) => {
                            updateInactiveMilitaryViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastName
                    placeholder={
                        intl.formatMessage({
                            id: "lastNamePlaceholder",
                            defaultMessage: "Last Name",
                        })}
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={
                        (newValue) => {
                            updateInactiveMilitaryViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <Email
                placeholder={
                    intl.formatMessage({
                        id: "emailAddressPlaceholder",
                        defaultMessage: "Email Address",
                    })}
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Personal email address is recommended"
                    />
                }
                onChange={
                    (newValue) => {
                        updateInactiveMilitaryViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={event => handleEmailOnKeyDown(event)}
            />

            <PostalCodeComponent
                isErrored={!!fieldValidationErrors.postalCode}
                placeholder={
                    intl.formatMessage({
                        id: "postalCodePlaceholder",
                        defaultMessage: "Home Zip Code",
                    })}
                onChange={(newValue: any) => {
                    updateInactiveMilitaryViewModel(
                        'metadata',
                        {
                            postalCode: newValue,
                        },
                    );
                    updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                }}
                value={viewModel.hasOwnProperty('metadata') ? viewModel.metadata.postalCode : undefined}
            />

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                onChange={(newValue) => {
                    updateInactiveMilitaryViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <DischargeDate
                isErrored={!!fieldValidationErrors.dischargeDate}
                onChange={(newValue) => {
                    updateInactiveMilitaryViewModel('dischargeDate', newValue);
                    updateFieldValidationErrorsByFieldId('dischargeDate', newValue, verificationService);
                }}
                value={viewModel.dischargeDate}
            />

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateInactiveMilitaryViewModel("metadata", {
                        ...verificationService.viewModel.metadata,
                        marketConsentValue: newValue,
                    });
                    updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectInactiveMilitaryPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={button => setRef('submitButton', button)}
                    >
                        { failedInstantMatch ?
                            <FormattedHTMLMessage
                                id="tryAgain"
                                defaultMessage="Try Again" />
                        :
                            <FormattedHTMLMessage
                                id="verifyMyMilitaryStatus"
                                defaultMessage="Verify My Military Status" />
                        }
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="militaryInfoShared"
                    defaultMessage="Information entered here will be used for verification purposes." />
            </FormFooter>
        </div>
    );
};


export const StepInactiveMilitaryPersonalInfoComponent = injectIntl(StepInactiveMilitaryPersonalInfo);
